import React, { useState } from 'react';
import { XIcon } from '@heroicons/react/solid';

const PRICING = {
  91: 30,
  181: 50,
  365: 60
};

const DURATION_LABELS = {
  91: '91 days (~3 months)',
  181: '181 days (~6 months)',
  365: '365 days (1 year)'
};

function PricingReviewModal({
  isOpen,
  onClose,
  onProceedToPayment,
  jobData,
  selectedBoards,
  duration,
  legacyBalance
}) {
  if (!isOpen) return null;

  // Calculate expiry date
  const calculateExpiryDate = (days) => {
    let today = new Date();
    return new Date(today.getTime() + (days + 1) * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const expiryDate = calculateExpiryDate(duration);

  // Calculate pricing
  const pricePerBoard = PRICING[duration];
  const boardCount = selectedBoards.length;
  const subtotal = boardCount * pricePerBoard;
  const discount = boardCount >= 2 ? subtotal * 0.10 : 0;
  const afterDiscount = subtotal - discount;
  const balanceApplied = Math.min(legacyBalance, afterDiscount);
  const total = Math.max(0, afterDiscount - balanceApplied);

  return (
    <div className="fixed z-10 inset-0 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen sm:pt-4 sm:px-4 sm:pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block align-bottom bg-white sm:rounded-lg px-4 pt-5 pb-4 text-left overflow-y-auto shadow-xl transform transition-all min-h-screen sm:min-h-0 w-full sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
              onClick={onClose}
            >
              <XIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="w-full mt-3 text-center sm:mt-0 sm:text-left">
              <h3 className="text-lg leading-6 font-bold text-gray-900 mb-6">
                Confirm & Pay
              </h3>

              {/* Details Section */}
              <div className="mb-6 bg-red-50 border-2 border-red-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Details</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Job Title</span>
                    <span className="text-sm font-medium text-gray-900">{jobData.title}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Post Duration</span>
                    <span className="text-sm font-medium text-gray-900">{DURATION_LABELS[duration]}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Expires On</span>
                    <span className="text-sm font-medium text-gray-900">{formatDate(expiryDate)}</span>
                  </div>
                </div>
              </div>

              {/* Publishing Section */}
              <div className="mb-6 bg-red-50 border-2 border-red-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Publishing To</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedBoards.map(board => (
                    <div
                      key={board}
                      className="inline-flex items-center px-4 py-2 border border-primary rounded-md shadow-sm text-sm font-medium text-primary bg-white"
                    >
                      {board.replace('JobLeads', ' Job Leads')}
                    </div>
                  ))}
                </div>
              </div>

              {/* Pricing Breakdown */}
              <div className="bg-red-50 border-2 border-red-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Pricing Breakdown</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-gray-700">
                    <span>{boardCount} board{boardCount > 1 ? 's' : ''} × ${pricePerBoard}</span>
                    <span className="font-medium">${subtotal.toFixed(2)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-green-600 font-medium">
                      <span>Multi-board discount (10%)</span>
                      <span>-${discount.toFixed(2)}</span>
                    </div>
                  )}
                  {balanceApplied > 0 && (
                    <div className="flex justify-between text-primary font-medium">
                      <span>Account balance</span>
                      <span>-${balanceApplied.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-primary pt-3 border-t-2 border-red-200 mt-2 text-lg">
                    <span>Amount Due</span>
                    <span>${total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-6 sm:flex sm:flex-row-reverse gap-3">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-red-700 focus:outline-none sm:w-auto sm:text-sm"
                  onClick={onProceedToPayment}
                >
                  {total > 0 ? 'Proceed to Payment' : 'Publish Job'}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:w-auto sm:text-sm"
                  onClick={onClose}
                >
                  Go Back
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PricingReviewModal;
